import { createFileRoute } from "@tanstack/react-router";
import { LocaleLink } from "@/components/site/LocaleLink";
import { lazy, Suspense } from "react";
import {
  Radio,
  BarChart3,
  AlertTriangle,
  ArrowRight,
  Landmark,
  ShoppingBag,
  Pill,
  Store,
  Car,
  Megaphone,
  Star,
  Building2,
  Network,
  Users,
  FileText,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { MetricsStrip } from "@/components/site/MetricsStrip";
import { ClientLogoStrip } from "@/components/site/ClientLogoStrip";
import { Reveal, RevealItem } from "@/components/site/Reveal";
import { REPORTS, REPORT_BASE } from "@/content/reports";
import { REPORT_DETAIL_SLUGS } from "@/content/report-details-slugs";
import { useT } from "@/lib/i18n/I18nProvider";
import { localizeTopic } from "@/content/topic-i18n";
import { useNewReports } from "@/hooks/use-new-reports";
import { NewReportBadge } from "@/components/site/NewReportBadge";
import amecLogo from "@/assets/associations/amec.webp";
import fibepLogo from "@/assets/associations/fibep.webp";
import bdvoLogo from "@/assets/associations/bdvo.webp";
import kidaLogo from "@/assets/associations/kida.webp";
import updataOneLogo from "@/assets/associations/updata-one.webp";

const MentionTicker = lazy(() =>
  import("@/components/site/MentionTicker").then((m) => ({ default: m.MentionTicker })),
);
import { HowItWorksSkeleton } from "@/components/site/HowItWorks";
import { PlansTeaserSkeleton } from "@/components/site/PlansTeaser";

import { pageSchemas } from "@/lib/seo/page-schema";
const HowItWorks = lazy(() =>
  import("@/components/site/HowItWorks").then((m) => ({ default: m.HowItWorks })),
);
const PlansTeaser = lazy(() =>
  import("@/components/site/PlansTeaser").then((m) => ({ default: m.PlansTeaser })),
);
const PlansTeaserBG = lazy(() =>
  import("@/components/site/PlansTeaserBG").then((m) => ({ default: m.PlansTeaserBG })),
);

const OG_IMAGE = "https://perceptica.com/og-image.jpg";

import { Index } from "@/components/site/HomePage";
export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Media Monitoring in Bulgaria & Southeast Europe | Perceptica" },
      {
        name: "description",
        content:
          "Perceptica is the media monitoring platform for Southeast Europe - combining media intelligence, media analysis and media listening in one place. Book a demo.",
      },
      { property: "og:title", content: "Media Monitoring in Bulgaria & Southeast Europe | Perceptica" },
      {
        property: "og:description",
        content: "Perceptica is the media monitoring platform for Southeast Europe - combining media intelligence, media analysis and media listening in one place. Book a demo.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Media Monitoring in Bulgaria & Southeast Europe | Perceptica" },
      {
        name: "twitter:description",
        content: "Perceptica is the media monitoring platform for Southeast Europe - combining media intelligence, media analysis and media listening in one place. Book a demo.",
      },
      { name: "twitter:image", content: OG_IMAGE },
      { property: "og:url", content: "https://perceptica.com/" },
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/", collection: { name: "Perceptica", description: "Media monitoring & intelligence for SEE" } }),
  }),
  component: Index,
});
